'use client'

import { Card } from '@repo/web-ui/components/card'
import { useTranslations } from 'next-intl'
import * as React from 'react'
import { Typography } from '@repo/web-ui/components/typography'
import { CheckCircleInvertedIcon } from 'primitives/icons'
import { useHasAllAccessWithPending } from '@repo/cf-user/contexts/auth'
import { sendAmplitudeEvent } from '@repo/analytics/amplitude/utils'
import { PAYMENT_ENTRY_POINTS } from 'payments/payments-dialog/analytics'

import { FreeTrialIllustration } from './illustration/free-trial-illustration'
import { Button } from '@repo/design-system-cf/components/button'
import { myAccountTrackingEvents } from '../tracking/my-account-tracking-events'
import { useFeatureIsOn } from 'growthbook'
import { useUserIsEligibleForAllAccessFreeTrialQuery } from '@repo/cf-user/hooks/use-user-data'
import { StartFreeTrialButton } from 'payments/start-free-trial-button'

export const FreeTrialBanner = ({
  showFreeTrial,
  featureFlag,
}: {
  showFreeTrial?: boolean
  featureFlag?: string
}) => {
  const t = useTranslations('my_account.free_trial_banner')
  const { hasAllAccess, isPending: isLoadingAllAccess } = useHasAllAccessWithPending()
  const featureEnabled = useFeatureIsOn(featureFlag ?? '')
  const { data: isEligibleForAllAccessFreeTrial } = useUserIsEligibleForAllAccessFreeTrialQuery()

  const usps = React.useMemo(() => {
    return [t('usp_1'), t('usp_2'), t('usp_3')]
  }, [t])

  React.useEffect(() => {
    if (!hasAllAccess && !isLoadingAllAccess) {
      sendAmplitudeEvent(myAccountTrackingEvents.MY_ACCOUNT_FREE_TRIAL_BANNER_SHOWN)
    }
  }, [hasAllAccess, isLoadingAllAccess])

  // oxlint-disable-next-line require-await
  const handleFreeTrialClick = React.useCallback(async () => {
    sendAmplitudeEvent(myAccountTrackingEvents.MY_ACCOUNT_FREE_TRIAL_BANNER_CLICKED)
  }, [])

  if (
    !isEligibleForAllAccessFreeTrial ||
    hasAllAccess ||
    isLoadingAllAccess ||
    (!showFreeTrial && !featureEnabled)
  ) {
    return null
  }

  return (
    <Card className="!rounded-2 flex gap-6 [background:linear-gradient(180deg,_#F9F5EF_69.86%,_#FFF0DB_100%),_var(--CF-Grey-0,theme('colors.cf-white'))] md:flex-row lg:flex-nowrap lg:gap-10 lg:py-6">
      <div className="hidden self-center md:block">
        <FreeTrialIllustration className="h-40 w-40" />
      </div>
      <div className="flex min-w-0 w-full flex-col gap-4 md:flex-1">
        <div className="flex flex-col gap-2">
          <Typography variant="heading-4" className="text-cf-sf-gray-300">
            {t('sub_heading')}
          </Typography>
          <Typography variant="heading-2">{t('heading')}</Typography>
        </div>
        <ul className="flex flex-col gap-3">
          {usps.map(usp => (
            <li key={usp} className="flex items-center gap-3">
              <CheckCircleInvertedIcon className="size-6 shrink-0" />
              {usp}
            </li>
          ))}
        </ul>
        <Button
          variant="primary-green-bright"
          className="text-cf-white hover:text-cf-white hover:bg-secondary-700 my-2 h-auto min-h-[45px] w-full self-stretch whitespace-normal break-words py-3 text-center font-bold uppercase sm:h-[45px] sm:w-auto sm:self-start sm:whitespace-nowrap"
          asChild
        >
          <StartFreeTrialButton
            onClick={handleFreeTrialClick}
            entryPoint={PAYMENT_ENTRY_POINTS.MY_ACCOUNT_BANNER}
            label={t('cta')}
          />
        </Button>
      </div>
    </Card>
  )
}
